import { usePage } from "@inertiajs/react"
import { Button } from "../../components/ui/button"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table"
import { Trash2, ExternalLink, Edit, Power } from "lucide-react"
import { siGithub } from 'simple-icons'
import DataTablePagination from "../../components/ui/data-table-pagination"
import DataTableResults from "../../components/ui/data-table-results"
import Preloader from "../../components/ui/preloader"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog"
import EditProject from "./EditProject"
import { useState } from "react"
import { router } from "@inertiajs/react"
import { Project, PaginationData } from "../../types/project"

declare module '@inertiajs/core' {
  interface PageProps {
    projects: Project[]
    project_pagination: PaginationData
  }
}

const ProjectsList = () => {
  const { projects, project_pagination } = usePage().props
  const [isLoading, setIsLoading] = useState(false)
  const [editingProject, setEditingProject] = useState<Project | null>(null)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [deletingProject, setDeletingProject] = useState<Project | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const handlePageChange = (page: number) => {
    setIsLoading(true)
    router.get('/admin/projects', { page: page }, {
      preserveState: true,
      preserveScroll: true,
      onFinish: () => setIsLoading(false)
    })
  }

  const handleEdit = (project: Project) => {
    setEditingProject(project)
    setEditDialogOpen(true)
  }

  const handleEditSuccess = () => {
    setEditDialogOpen(false)
    setEditingProject(null)
  }

  const handleEditCancel = () => {
    setEditDialogOpen(false)
    setEditingProject(null)
  }

  const handleDelete = (project: Project) => {
    setDeletingProject(project)
    setDeleteDialogOpen(true)
  }

  const confirmDelete = () => {
    if (deletingProject) {
      router.delete(`/admin/projects/${deletingProject.id}`, {
        onSuccess: () => {
          setDeleteDialogOpen(false)
          setDeletingProject(null)
        }
      })
    }
  }

  const cancelDelete = () => {
    setDeleteDialogOpen(false)
    setDeletingProject(null)
  }

  const handleToggleStatus = (id: number) => {
    if (confirm('Are you sure you want to update this project\'s status?')) {
      router.patch(`/admin/projects/${id}/toggle_status`, {}, {
        preserveState: true,
        preserveScroll: true
      })
    }
  }

  // GitHub icon component
  const GithubIcon = ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d={siGithub.path} />
    </svg>
  )

  return (
    <div className="space-y-4">
      {projects.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          No projects found. Add your first project to get started.
        </div>
      ) : (
        <>
          <DataTableResults pagination={project_pagination} object_name="projects" />
          
          <Preloader isLoading={isLoading}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Technologies</TableHead>
                  <TableHead>Links</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Featured</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {projects.map((project) => (
                  <TableRow key={project.id}>
                    <TableCell className="font-medium">{project.title}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {project.tech_stack.split(',').map((tech, index) => (
                          <span
                            key={index}
                            className="bg-secondary text-secondary-foreground px-2 py-1 rounded-md text-xs"
                          >
                            {tech.trim()}
                          </span>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        {project.github_url && (
                          <Button variant="outline" size="sm" asChild>
                            <a href={project.github_url} target="_blank" rel="noopener noreferrer">
                              <GithubIcon className="h-4 w-4" />
                            </a>
                          </Button>
                        )}
                        {project.demo_url && (
                          <Button variant="outline" size="sm" asChild>
                            <a href={project.demo_url} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          </Button>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded-md text-xs ${
                          project.status === 'In Production' ? 'bg-green-100 text-green-800' :
                          project.status === 'Active Development' ? 'bg-blue-100 text-blue-800' :
                          project.status === 'Maintenance' ? 'bg-yellow-100 text-yellow-800' :
                          project.status === 'Open Source' ? 'bg-purple-100 text-purple-800' :
                          project.status === 'Prototype' ? 'bg-orange-100 text-orange-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {project.status}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleToggleStatus(project.id)}
                          title="Click to cycle through project statuses"
                        >
                          <Power className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>
                      {project.featured ? (
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-md text-xs">
                          Featured
                        </span>
                      ) : (
                        <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded-md text-xs">
                          Not Featured
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-2 justify-end">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(project)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDelete(project)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Preloader>
          
          <DataTablePagination
            pagination={project_pagination}
            onPageChange={handlePageChange}
          />

          <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Edit Project</DialogTitle>
                <DialogDescription>
                  Update the project details below. All fields marked with * are required.
                </DialogDescription>
              </DialogHeader>
              {editingProject && (
                <EditProject
                  project={editingProject}
                  onSuccess={handleEditSuccess}
                  onCancel={handleEditCancel}
                />
              )}
            </DialogContent>
          </Dialog>

          <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Delete Project</DialogTitle>
                <DialogDescription>
                  Are you sure you want to delete "{deletingProject?.title}"? This action cannot be undone.
                </DialogDescription>
              </DialogHeader>
              <div className="flex gap-3 justify-end">
                <Button variant="outline" onClick={cancelDelete}>
                  Cancel
                </Button>
                <Button variant="destructive" onClick={confirmDelete}>
                  Delete
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  )
}

export default ProjectsList